/**
 * AA Callback Handler Component
 *
 * Handles the redirect callback from Saafe after the AA consent flow.
 * Displays appropriate UI for success, error, and processing states.
 *
 * This component uses the useAACallback hook to parse URL parameters
 * and fetch updated AA status from the BFF.
 *
 * URL Parameters from Saafe redirect:
 * - Success: ?aa_status=success&linked_accounts=N
 * - Error: ?aa_status=error&aa_error=message&aa_error_code=CODE
 *
 * @example
 * ```tsx
 * // In the /aa/callback route
 * <AACallbackHandler />
 *
 * // With auto-navigation and callbacks
 * <AACallbackHandler
 *   autoNavigate
 *   autoNavigateDelay={3000}
 *   onSuccess={(count) => console.log(`Linked ${count} accounts`)}
 *   onError={(error) => console.error(error)}
 * />
 * ```
 */

import { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { useAACallback, useHomeData, type ValidRoutePath } from "@/hooks";
import { cn } from "@/lib/utils";
import { CelebrationAnimation } from "./CelebrationAnimation";
import { PortfolioSuccessSummary } from "./PortfolioSuccessSummary";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  ArrowRight,
  AlertCircle,
  Home,
  LinkIcon,
  ShieldOff,
  ChevronDown,
  Headphones,
  Lock,
} from "lucide-react";
import { TRUST_COPY } from "@/constants/trustCopy";

// ============================================================================
// Types
// ============================================================================

export interface AACallbackHandlerProps {
  /** Path to navigate to when retrying the flow (default: "/") */
  retryPath?: ValidRoutePath;
  /** Optional callback when import is successful */
  onSuccess?: (linkedAccountsCount: number) => void;
  /** Optional callback when import fails */
  onError?: (error: string) => void;
  /** Auto-navigate to portfolio after success (default: false) */
  autoNavigate?: boolean;
  /** Delay in ms before auto-navigation (default: 3000) */
  autoNavigateDelay?: number;
  /** Additional CSS classes for the container */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

const FETCH_PROGRESS_STAGES = [
  { delay: 0, message: "Connecting to Account Aggregator..." },
  { delay: 5000, message: "Getting data from CAMS..." },
  { delay: 15000, message: "Getting data from KFintech..." },
  { delay: 25000, message: "Processing your holdings..." },
] as const;

function getErrorUserMessage(errorCode: string | null): string {
  if (!errorCode) return "An unexpected error occurred. Please try again.";
  const code = errorCode.toUpperCase();
  if (code.includes("NETWORK") || code.includes("FETCH"))
    return "Connection was interrupted. Please try again.";
  if (code.includes("TIMEOUT"))
    return "The bank is taking too long. Please try again.";
  if (code.includes("SERVER") || code.includes("5XX") || code.includes("500"))
    return "Our servers are having issues. Please try in a few minutes.";
  return "An unexpected error occurred. Please try again.";
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Data fetching progress with staged messages
 */
function DataFetchingProgress() {
  const [stageIndex, setStageIndex] = useState(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 1; i < FETCH_PROGRESS_STAGES.length; i++) {
      const stage = FETCH_PROGRESS_STAGES[i];
      if (stage) {
        timers.push(setTimeout(() => setStageIndex(i), stage.delay));
      }
    }
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div
      className="flex flex-col items-center justify-center"
      role="status"
      aria-live="polite"
      aria-label="Processing account linking"
    >
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center py-12">
          {/* Animated loading indicator */}
          <div className="relative">
            <div className="absolute inset-0 animate-ping rounded-full bg-primary/20" />
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
            </div>
          </div>
          <h2 className="mt-8 text-xl font-semibold text-foreground">
            Linking Your Accounts
          </h2>
          <p
            className="mt-2 max-w-sm text-center text-muted-foreground transition-opacity duration-300"
            aria-live="polite"
          >
            {FETCH_PROGRESS_STAGES[stageIndex]?.message}
          </p>
          {/* Step indicators */}
          <div className="mt-6 flex gap-2">
            {FETCH_PROGRESS_STAGES.map((_, i) => (
              <div
                key={i}
                className={cn(
                  "h-1.5 w-8 rounded-full transition-colors duration-500",
                  i <= stageIndex ? "bg-primary" : "bg-muted"
                )}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Success state with celebration UI
 */
interface SuccessStateProps {
  linkedAccountsCount: number | null;
  onContinue: () => void;
  isAutoNavigating?: boolean;
  autoNavigateCountdown?: number;
}

function SuccessState({
  linkedAccountsCount,
  onContinue,
  isAutoNavigating,
  autoNavigateCountdown,
}: SuccessStateProps) {
  const count = linkedAccountsCount ?? 0;
  const accountText = count === 1 ? "account" : "accounts";
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [celebrationDone, setCelebrationDone] = useState(false);

  // Fetch portfolio data — graceful if unavailable
  const { data: homeData } = useHomeData();

  const handleCelebrationComplete = useCallback(() => {
    setCelebrationDone(true);
  }, []);

  // Focus the CTA button after celebration completes
  useEffect(() => {
    if (celebrationDone) {
      buttonRef.current?.focus();
    }
  }, [celebrationDone]);

  return (
    <div
      className="flex flex-col items-center justify-center gap-6"
      role="status"
      aria-live="polite"
    >
      {/* SR announcement */}
      <span className="sr-only">
        Accounts linked successfully.
        {homeData?.portfolio
          ? ` Portfolio value: ${homeData.portfolio.totalValue}`
          : ""}
      </span>

      <Card className="w-full max-w-md border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20">
        <CardContent className="flex flex-col items-center py-12">
          {/* Celebration animation replacing static icon */}
          <CelebrationAnimation onComplete={handleCelebrationComplete} />

          <h2 className="mt-8 text-xl font-semibold text-green-700 dark:text-green-400">
            Accounts Linked Successfully!
          </h2>

          {/* Linked accounts count badge */}
          {count > 0 && (
            <div className="mt-4 flex items-center gap-2 rounded-full bg-green-100 px-4 py-2 dark:bg-green-900/50">
              <LinkIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-lg font-bold text-green-700 dark:text-green-400">
                {count}
              </span>
              <span className="text-sm text-green-600 dark:text-green-400">
                {accountText} connected
              </span>
            </div>
          )}

          <p className="mt-4 max-w-sm text-center text-muted-foreground">
            Your portfolio data is now being imported. You can view your
            holdings and get personalized recommendations.
          </p>

          <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" />
            <span>{TRUST_COPY.postImport.body}</span>
          </div>

          {isAutoNavigating && autoNavigateCountdown !== undefined && (
            <p className="mt-3 text-sm text-muted-foreground">
              Redirecting to portfolio in {autoNavigateCountdown}s...
            </p>
          )}
        </CardContent>
        <CardFooter className="flex justify-center pb-8">
          <Button
            ref={buttonRef}
            onClick={onContinue}
            size="lg"
            className="gap-2"
          >
            View Portfolio
            <ArrowRight className="h-4 w-4" />
          </Button>
        </CardFooter>
      </Card>

      {/* Portfolio summary preview */}
      {homeData?.portfolio && homeData?.holdings && (
        <PortfolioSuccessSummary
          portfolio={homeData.portfolio}
          holdings={homeData.holdings}
          topPerformers={homeData.topPerformers}
        />
      )}
    </div>
  );
}

/**
 * Rejected/cancelled state with empathetic copy
 */
interface RejectedStateProps {
  onTryAgain: () => void;
  onMaybeLater: () => void;
}

function RejectedState({ onTryAgain, onMaybeLater }: RejectedStateProps) {
  return (
    <div
      className="flex flex-col items-center justify-center"
      role="status"
      aria-live="polite"
    >
      <Card className="w-full max-w-md border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
        <CardContent className="flex flex-col items-center py-12">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/50">
            <ShieldOff className="h-10 w-10 text-amber-600 dark:text-amber-400" />
          </div>

          <h2 className="mt-8 text-xl font-semibold text-amber-700 dark:text-amber-400">
            You chose not to share your data
          </h2>

          <p className="mt-4 max-w-sm text-center text-muted-foreground">
            That's okay! You can always import later.
          </p>

          <p className="mt-2 text-sm text-muted-foreground/70">
            No data was accessed or stored.
          </p>
        </CardContent>
        <CardFooter className="flex flex-col gap-3 pb-8 sm:flex-row sm:justify-center">
          <Button onClick={onTryAgain} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
          <Button variant="outline" onClick={onMaybeLater} className="gap-2">
            <Home className="h-4 w-4" />
            Maybe Later
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

/**
 * Error state with error-specific messages and escalating recovery
 */
interface ErrorStateProps {
  errorMessage: string | null;
  errorCode?: string | null;
  retryCount: number;
  isRetrying: boolean;
  onRetry: () => void;
  onGoHome: () => void;
}

function ErrorState({
  errorMessage,
  errorCode,
  retryCount,
  isRetrying,
  onRetry,
  onGoHome,
}: ErrorStateProps) {
  const [showDetails, setShowDetails] = useState(false);
  const displayMessage =
    errorMessage || getErrorUserMessage(errorCode ?? null);

  const supportProminent = retryCount >= 2;
  const showSupport = retryCount >= 1;

  const retryButton = (
    <Button
      onClick={onRetry}
      disabled={isRetrying}
      variant={supportProminent ? "outline" : "default"}
      className="gap-2"
    >
      {isRetrying ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Retrying...
        </>
      ) : (
        <>
          <RefreshCw className="h-4 w-4" />
          Try Again
        </>
      )}
    </Button>
  );

  const supportButton = (
    <Button
      variant={supportProminent ? "default" : "outline"}
      onClick={onGoHome}
      className="gap-2"
    >
      <Headphones className="h-4 w-4" />
      Contact Support
    </Button>
  );

  return (
    <div
      className="flex flex-col items-center justify-center"
      role="alert"
      aria-live="assertive"
    >
      <Card className="w-full max-w-md border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20">
        <CardContent className="flex flex-col items-center py-12">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/50">
            <XCircle className="h-10 w-10 text-red-600 dark:text-red-400" />
          </div>

          <h2 className="mt-8 text-xl font-semibold text-red-700 dark:text-red-400">
            Something Went Wrong
          </h2>

          <p className="mt-4 max-w-sm text-center text-muted-foreground">
            {displayMessage}
          </p>

          {errorCode && (
            <button
              onClick={() => setShowDetails((v) => !v)}
              className="mt-3 flex items-center gap-1 text-xs text-muted-foreground/70 hover:text-muted-foreground"
            >
              Technical details
              <ChevronDown
                className={cn(
                  "h-3 w-3 transition-transform",
                  showDetails && "rotate-180"
                )}
              />
            </button>
          )}
          {showDetails && errorCode && (
            <p className="mt-1 text-xs text-muted-foreground/70">
              Error code: {errorCode}
            </p>
          )}
        </CardContent>
        <CardFooter className="flex flex-col gap-3 pb-8 sm:flex-row sm:justify-center">
          {supportProminent ? (
            <>
              {supportButton}
              {retryButton}
            </>
          ) : (
            <>
              {retryButton}
              {showSupport && supportButton}
            </>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}

/**
 * No callback state (user navigated here directly)
 */
function NoCallbackState({ onGoHome }: { onGoHome: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center py-12">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted">
            <AlertCircle className="h-10 w-10 text-muted-foreground" />
          </div>

          <h2 className="mt-8 text-xl font-semibold text-foreground">
            No Callback Data
          </h2>

          <p className="mt-4 max-w-sm text-center text-muted-foreground">
            It looks like you navigated here directly. Please start the account
            linking process from your portfolio page.
          </p>
        </CardContent>
        <CardFooter className="flex justify-center pb-8">
          <Button onClick={onGoHome} className="gap-2">
            <Home className="h-4 w-4" />
            Go to Portfolio
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AACallbackHandler({
  retryPath = "/",
  onSuccess,
  onError,
  autoNavigate = false,
  autoNavigateDelay = 3000,
  className,
}: AACallbackHandlerProps) {
  const {
    hasCallback,
    callbackResult,
    isSuccess,
    isError,
    isProcessing,
    isRetrying,
    retryCount,
    aaStatus,
    errorMessage,
    navigateToPortfolio,
    navigateTo,
    retryStatusFetch,
    clearCallbackParams,
  } = useAACallback();

  // Auto-navigate countdown state
  const [countdown, setCountdown] = useState<number | undefined>(undefined);

  // Track if callbacks have been called to prevent multiple calls
  const hasCalledSuccessRef = useRef(false);
  const hasCalledErrorRef = useRef(false);

  // Get linked accounts count from either fetched status or callback result
  const linkedAccountsCount =
    aaStatus?.linkedAccountsCount ?? callbackResult.linkedAccountsCount ?? 0;

  // Determine if this is a user-cancelled/rejected error
  const isCancelled =
    isError &&
    (callbackResult.errorCode === "USER_CANCELLED" ||
      callbackResult.errorCode === "CONSENT_DENIED" ||
      (errorMessage?.toLowerCase().includes("cancel") ?? false) ||
      (errorMessage?.toLowerCase().includes("denied") ?? false));

  // Handle success callback - only call once
  useEffect(() => {
    if (isSuccess && onSuccess && !hasCalledSuccessRef.current) {
      hasCalledSuccessRef.current = true;
      onSuccess(linkedAccountsCount);
    }
  }, [isSuccess, linkedAccountsCount, onSuccess]);

  // Handle error callback - only call once
  useEffect(() => {
    if (isError && onError && errorMessage && !hasCalledErrorRef.current) {
      hasCalledErrorRef.current = true;
      onError(errorMessage);
    }
  }, [isError, errorMessage, onError]);

  // Fetch home data to check if portfolio preview will be shown
  const { data: homeDataForDelay } = useHomeData();
  const hasPortfolioPreview = !!(
    homeDataForDelay?.portfolio && homeDataForDelay?.holdings
  );

  // Auto-navigate countdown (extend to 5s when portfolio preview is shown)
  useEffect(() => {
    if (!autoNavigate || !isSuccess) return;

    const effectiveDelay = hasPortfolioPreview
      ? Math.max(autoNavigateDelay, 5000)
      : autoNavigateDelay;
    const countdownSeconds = Math.ceil(effectiveDelay / 1000);
    setCountdown(countdownSeconds);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === undefined || prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const timeout = setTimeout(() => {
      navigateToPortfolio();
    }, effectiveDelay);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [autoNavigate, autoNavigateDelay, hasPortfolioPreview, isSuccess, navigateToPortfolio]);

  // Handle retry - either retry the fetch or navigate to start flow again
  const handleRetry = () => {
    if (isSuccess) {
      retryStatusFetch();
    } else {
      clearCallbackParams();
      navigateTo(retryPath);
    }
  };

  const handleContinue = () => {
    navigateToPortfolio();
  };

  const handleGoHome = () => {
    navigateTo("/");
  };

  const handleTryAgain = () => {
    clearCallbackParams();
    navigateTo(retryPath);
  };

  const containerClasses = cn(
    "container mx-auto flex min-h-[60vh] max-w-3xl items-center justify-center px-4 py-12",
    className
  );

  if (!hasCallback) {
    return (
      <div className={containerClasses}>
        <NoCallbackState onGoHome={handleGoHome} />
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className={containerClasses}>
        <DataFetchingProgress />
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className={containerClasses}>
        <SuccessState
          linkedAccountsCount={linkedAccountsCount}
          onContinue={handleContinue}
          isAutoNavigating={autoNavigate}
          autoNavigateCountdown={countdown}
        />
      </div>
    );
  }

  if (isCancelled) {
    return (
      <div className={containerClasses}>
        <RejectedState
          onTryAgain={handleTryAgain}
          onMaybeLater={handleGoHome}
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={containerClasses}>
        <ErrorState
          errorMessage={errorMessage}
          errorCode={callbackResult.errorCode}
          retryCount={retryCount}
          isRetrying={isRetrying}
          onRetry={handleRetry}
          onGoHome={handleGoHome}
        />
      </div>
    );
  }

  // Fallback to processing state
  return (
    <div className={containerClasses}>
      <DataFetchingProgress />
    </div>
  );
}

export default AACallbackHandler;
